import { useCallback, useEffect, useRef, useState } from 'react'
import { Typography, FormControl, styled, Stack, Checkbox } from '@mui/material'
// Components
import { PrimaryButton } from '$common/buttons/primary-button'
import { PhoneInput, PhoneInputRef } from '$common/input'
import { AuthThrottleWarningBanner } from '$common/auth-throttle-warning-banner'
import { isSignInFailure, signInUser } from '$logic/auth'
import {
  getThrottleBlock,
  isThrottleFailure,
  persistThrottleBlock,
  ThrottleBlock,
} from '$logic/auth/throttle'
import { goToAuthVerify } from '$logic/router'
import { isPhoneValidByLib, normalizePhone } from '$func/phone'
// Localization
import { t } from '$i18n'
import { isContactPermissionAllowedByClientUser } from '$logic/twaCommunication'
import { changeAutoSyncContacts } from '$logic/contacts/service'
import { showSwitchAutoSyncActivationSnackbar } from '$comp/snackbars'
import { useCountDownToDate } from '$hooks/use-count-down-to-date'
import { dateWithOffset } from '$logic/client-device/time-offset.service'

const Fields = styled('div')<{ hasError?: boolean }>`
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-bottom: auto;
`

function toLocalPhone(fullPhone?: string): string {
  if (!fullPhone) return ''
  return fullPhone.startsWith('+98') ? fullPhone.slice(3) : fullPhone
}

export function SignIn({ phone: initialPhone }: { phone?: string } = {}) {
  const phoneInputRef = useRef<PhoneInputRef>(null)

  // TODO: after new design values like phone can move to phone-input component and be received via useImperativeHandle
  const [phone, setPhone] = useState(toLocalPhone(initialPhone))
  const [alertText, setAlertText] = useState('')
  const [syncContactsActivated, setSyncContactsActivated] = useState(false)
  const [throttleBlock, setThrottleBlock] = useState<ThrottleBlock | null>(null)
  const throttleRemainingSeconds = useCountDownToDate(
    throttleBlock?.retryUntil ?? 0,
  )
  const isThrottled = throttleRemainingSeconds > 0

  useEffect(() => {
    const fullPhone = phoneInputRef.current?.getPhone(phone) ?? phone
    setThrottleBlock(getThrottleBlock('signin', normalizePhone(fullPhone)))
  }, [phone])

  useEffect(() => {
    if (throttleBlock && throttleBlock.retryUntil <= dateWithOffset()) {
      setThrottleBlock(null)
    }
  }, [throttleBlock, throttleRemainingSeconds])

  const toggleContactActivation = () => {
    setSyncContactsActivated(prevSyncContactsActivated => {
      analite('SIGN_IN_SYNC_CONTACTS', {
        active: !prevSyncContactsActivated,
      })
      void changeAutoSyncContacts(!prevSyncContactsActivated)
      showSwitchAutoSyncActivationSnackbar(!prevSyncContactsActivated)
      return !prevSyncContactsActivated
    })
  }
  const isSubmittedRef = useRef<boolean>(false)
  const isContactPermissionAllowed = isContactPermissionAllowedByClientUser()
  const onSubmit = useCallback(async () => {
    if (isSubmittedRef.current || isThrottled) return
    isSubmittedRef.current = true
    try {
      if (!phone) {
        setAlertText(t('phoneInput.errorMessage'))
        return
      }

      const fullNumber = phoneInputRef.current?.getPhone(phone)
      if (!fullNumber) return

      const isValid = isPhoneValidByLib(fullNumber)
      if (!isValid) {
        setAlertText(t('signin.serverCodes.INVALID_PHONE'))
        return
      }

      const normalized = normalizePhone(fullNumber)
      setAlertText('')
      const verificationSession = await signInUser(fullNumber)
      if (isThrottleFailure(verificationSession)) {
        setThrottleBlock(
          persistThrottleBlock(
            'signin',
            normalized,
            verificationSession.throttle,
          ),
        )
        return
      }

      if (isSignInFailure(verificationSession)) {
        setAlertText(verificationSession.errorMessage)
        return
      }

      goToAuthVerify(verificationSession)
    } finally {
      isSubmittedRef.current = false
    }
  }, [phone, isThrottled])

  const onPhoneChange = useCallback(
    (phoneNumber: string) => {
      if (isSubmittedRef.current) isSubmittedRef.current = false
      if (alertText) {
        setAlertText('')
      }
      setPhone(phoneNumber)
    },
    [alertText],
  )

  return (
    <Stack padding="64px 24px 24px" height="100%">
      <Fields hasError={!!alertText}>
        <img
          src="/assets/icons/icon.svg"
          style={{ maxHeight: '88px' }}
          alt="AlphaSchool Logo"
        />
        <Typography
          color="secondary700"
          variant="subtitle1"
          mt="48px"
          mb="16px"
          textAlign="center"
          width="100%"
        >
          {t('signin.enterPhone')}
        </Typography>
        {/* TODO logic for hasError prop  */}
        <FormControl
          sx={{ width: '100%' }}
          onSubmit={onSubmit}
          onKeyDown={e => {
            if (e.key === 'Enter') onSubmit()
          }}
        >
          <PhoneInput
            value={phone}
            onChange={onPhoneChange}
            hasError={!!alertText}
            errorMessage={alertText}
            ref={phoneInputRef}
          />
        </FormControl>
        {isContactPermissionAllowed && (
          <Stack direction="row" gap="6px">
            <Checkbox
              size="small"
              checked={syncContactsActivated}
              onChange={toggleContactActivation}
              sx={{
                color: 'neutral300',
                // '.MuiCheckbox-root': {
                padding: '2px',
                // },
              }}
            />
            <Typography variant="body2">
              {t('signin.checkboxOption')}
            </Typography>
          </Stack>
        )}
      </Fields>
      <AuthThrottleWarningBanner
        scope={throttleBlock?.scope}
        remainingMillis={throttleRemainingSeconds * 1000}
      />
      <PrimaryButton
        type="submit"
        text={t('signin.confirm')}
        onClick={onSubmit}
        disabled={!phone.length || isThrottled}
      />
    </Stack>
  )
}
